/**
 * SupplierFayresPage — /dashboard/supplier/fayres
 *
 * Supplier view of the fayre world. Two lists:
 *   1. Joined fayres — with per-fayre metrics (chats count)
 *   2. Nearby unjoined fayres — one-tap "Join Free" (or paid tier placeholder)
 *
 * Payment is deliberately not wired up this slice; the Featured/Premium
 * placeholders communicate future value without blocking the flow.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, CalendarBlank, Users, ChatCircle, Storefront, CaretRight,
  Lock, Star, CheckCircle,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { ShareFayreButton } from '../components/ShareFayreButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch {
    return iso;
  }
};

const SupplierFayresPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [joined, setJoined] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [upsellOpen, setUpsellOpen] = useState(null); // fayre being upsold

  const load = () => {
    axios.get(`${API}/suppliers/me/fayres`)
      .then((r) => { setJoined(r.data.joined || []); setNearby(r.data.nearby || []); })
      .catch(() => toast.error('Could not load your fayres'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // Wait for AuthContext to finish checking `/auth/me` before deciding
    // whether to redirect. Otherwise a hard refresh kicks the user out
    // even though they're still signed in (loading=true, user=null for
    // ~200ms on mount).
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const joinFayre = async (fayreId) => {
    try {
      await axios.post(`${API}/fayres/${fayreId}/join`);
      toast.success("You're in! 🎉");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not join fayre');
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1014] text-white pb-12" data-testid="supplier-fayres-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0F1014]/95 backdrop-blur border-b border-white/5 px-5 py-3 flex items-center gap-3">
        <button onClick={() => navigate('/dashboard/supplier')} className="text-white/60 hover:text-white" data-testid="fayres-back-btn">
          <ArrowLeft size={16} weight="bold" />
        </button>
        <div className="flex-1">
          <p className="text-[10px] font-black uppercase tracking-[3px] text-[#C29D52]/80">Wedding Fayres</p>
          <p className="font-bold text-sm">Your fayres</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-6">
        {loading && <p className="text-white/40 text-sm text-center py-16" data-testid="fayres-page-loading">Loading…</p>}

        {!loading && (
          <>
            {/* Joined section */}
            <section data-testid="joined-section">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="text-xl font-black">Your upcoming fayres</h2>
                <p className="text-[11px] text-white/40">{joined.length} joined</p>
              </div>
              {!joined.length ? (
                <div className="rounded-xl p-6 bg-white/[0.03] border border-white/10 text-center" data-testid="joined-empty">
                  <Storefront size={24} weight="fill" className="mx-auto mb-2 text-white/20" />
                  <p className="font-bold text-sm text-white/70">You haven't joined a fayre yet</p>
                  <p className="text-white/40 text-xs mt-1">Pick one from below — your profile is ready to go.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {joined.map((f) => (
                    <JoinedFayreCard key={f.id} f={f} />
                  ))}
                </div>
              )}
            </section>

            {/* Nearby section */}
            <section className="mt-10" data-testid="nearby-section">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="text-xl font-black">Fayres near you</h2>
                <p className="text-[11px] text-white/40">{nearby.length} available</p>
              </div>
              {!nearby.length ? (
                <div className="rounded-xl p-6 bg-white/[0.03] border border-white/10 text-center" data-testid="nearby-empty">
                  <p className="font-bold text-sm text-white/70">No fayres in your area yet</p>
                  <p className="text-white/40 text-xs mt-1">New events added weekly — we'll let you know.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {nearby.map((f) => (
                    <NearbyFayreCard
                      key={f.id}
                      f={f}
                      onJoinFree={() => joinFayre(f.id)}
                      onUpgrade={() => setUpsellOpen(f)}
                    />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {upsellOpen && (
        <TierUpsellModal fayre={upsellOpen} onClose={() => setUpsellOpen(null)} onJoinFree={() => { joinFayre(upsellOpen.id); setUpsellOpen(null); }} />
      )}
    </div>
  );
};

export default SupplierFayresPage;

// ── Card for a fayre the supplier has already joined ────────────────
const JoinedFayreCard = ({ f }) => {
  const navigate = useNavigate();
  const unread = Number(f.metrics?.new_messages || 0);
  // Whole-card click opens the fayre detail page. Inner buttons (Share)
  // stop propagation so they don't double-fire navigation. We also
  // fire the mark-seen endpoint so the unread-dot clears locally.
  const openDetail = () => {
    axios.post(`${API}/suppliers/me/fayres/${f.id}/seen`).catch(() => {});
    navigate(`/fayres/${f.id}`);
  };
  return (
    <div
      className="rounded-xl p-4 bg-white/[0.04] border border-white/10 hover:border-white/20 transition-all"
      data-testid={`joined-fayre-${f.id}`}
    >
      <button
        onClick={openDetail}
        className="w-full text-left flex items-start gap-3"
        data-testid={`joined-open-${f.id}`}
      >
        <div
          className="w-14 h-14 rounded-lg shrink-0 flex items-center justify-center text-2xl relative"
          style={{ background: 'linear-gradient(135deg, rgba(108,85,224,0.4), rgba(194,157,82,0.25))' }}
        >
          🎪
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center px-1 ring-2 ring-[#0F1014]"
              data-testid={`joined-unread-dot-${f.id}`}
              aria-label={`${unread} new message${unread === 1 ? '' : 's'}`}
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm leading-tight truncate flex items-center gap-1.5">
            {f.name}
            {unread > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[9px] font-black px-1.5 py-0.5 rounded-full bg-rose-500/15 border border-rose-400/40 text-rose-200"
                data-testid={`joined-unread-pill-${f.id}`}
              >
                💬 {unread} new
              </span>
            )}
          </p>
          <p className="text-white/40 text-[11px] mt-1 flex flex-wrap gap-x-2">
            <span className="inline-flex items-center gap-0.5"><CalendarBlank size={10} weight="bold" /> {formatDate(f.date)}</span>
            <span className="inline-flex items-center gap-0.5"><MapPin size={10} weight="bold" /> {f.location?.city}</span>
          </p>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-white/70">
            <span className="inline-flex items-center gap-1">
              <ChatCircle size={12} weight="fill" className="text-[#C29D52]" />
              <span className="font-bold">{f.metrics?.chats ?? 0}</span>
              <span className="text-white/40">chats</span>
            </span>
            {f.tier !== 'standard' && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-black bg-amber-400/15 text-amber-200 border border-amber-400/40"
                data-testid={`joined-tier-${f.id}`}
              >
                <Star size={9} weight="fill" /> {f.tier}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="inline-flex items-center gap-1 text-[10px] font-black text-emerald-300">
            <CheckCircle size={10} weight="fill" /> JOINED
          </span>
          <span className="text-[10px] text-white/30 font-bold">{f.supplier_count} suppliers</span>
        </div>
      </button>
      {/* Share → get more enquiries — stopPropagation so the inner
          click doesn't also bubble into the card-open button above. */}
      <div onClick={(e) => e.stopPropagation()}>
        <ShareFayreButton
          fayreId={f.id}
          fayreName={f.name}
          variant="supplier"
          data-testid={`joined-share-${f.id}`}
        />
      </div>
    </div>
  );
};

// ── Card for a nearby fayre the supplier hasn't joined ──────────────
const NearbyFayreCard = ({ f, onJoinFree, onUpgrade }) => {
  const navigate = useNavigate();
  const openDetail = () => navigate(`/fayres/${f.id}`);
  return (
    <div
      className="rounded-xl p-4 bg-white/[0.03] border border-white/10 hover:border-white/20 transition-all"
      data-testid={`nearby-fayre-${f.id}`}
    >
      <button
        onClick={openDetail}
        className="w-full text-left flex items-start gap-3"
        data-testid={`nearby-open-${f.id}`}
      >
        <div
          className="w-14 h-14 rounded-lg shrink-0 flex items-center justify-center text-2xl"
          style={{ background: 'linear-gradient(135deg, rgba(108,85,224,0.4), rgba(194,157,82,0.25))' }}
        >
          🎪
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm leading-tight">{f.name}</p>
          <p className="text-white/40 text-[11px] mt-1 flex flex-wrap gap-x-2">
            <span className="inline-flex items-center gap-0.5"><CalendarBlank size={10} weight="bold" /> {formatDate(f.date)}</span>
            <span className="inline-flex items-center gap-0.5"><MapPin size={10} weight="bold" /> {f.location?.city}</span>
            {f.distance_miles != null && <span className="text-white/30">· {f.distance_miles}mi</span>}
          </p>
          <p className="text-[11px] text-white/60 mt-1.5 flex items-center gap-1">
            <Users size={11} weight="fill" className="text-emerald-300" />
            <span className="font-bold">{f.supplier_count}</span>
            <span className="text-white/40">suppliers already attending</span>
          </p>
        </div>
      </button>
      {/* Action row — click handlers stop propagation so they don't
          also fire the card-open navigation above. */}
      <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onJoinFree}
          className="flex-1 py-2 rounded-lg text-[11px] font-black text-white flex items-center justify-center gap-1"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          data-testid={`join-free-${f.id}`}
        >
          Join Free <CaretRight size={10} weight="bold" />
        </button>
        <button
          onClick={onUpgrade}
          className="px-3 py-2 rounded-lg text-[11px] font-bold bg-amber-400/10 border border-amber-400/30 text-amber-200 hover:bg-amber-400/15 flex items-center gap-1"
          data-testid={`upsell-${f.id}`}
          title="Upgrade to Featured / Premium placement (coming soon)"
        >
          <Lock size={10} weight="fill" /> Upgrade
        </button>
      </div>
    </div>
  );
};

// ── Tier upsell modal (visual-only; payment not wired this slice) ───
const TierUpsellModal = ({ fayre, onClose, onJoinFree }) => (
  <div
    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-6"
    onClick={(e) => e.target === e.currentTarget && onClose()}
    data-testid="tier-upsell-modal"
  >
    <div className="w-full sm:max-w-md bg-[#15161B] border border-white/10 sm:rounded-2xl rounded-t-2xl overflow-hidden">
      <div className="px-5 pt-5 pb-3 border-b border-white/5">
        <p className="text-[11px] uppercase tracking-wider font-black text-[#C29D52]/85/80">Join {fayre.name}</p>
        <h3 className="font-black text-lg mt-0.5">Pick your booth</h3>
        <p className="text-white/50 text-xs mt-0.5">You can upgrade any time — start free.</p>
      </div>
      <div className="px-5 py-4 space-y-3">
        <TierOption
          name="Standard"
          price="Free"
          perks={['Listed in fayre', 'Standard ranking', 'Direct messages from couples']}
          onPick={onJoinFree}
          data-testid="tier-standard"
        />
        <TierOption
          name="Featured"
          price="£19"
          badge="🔥 Most popular"
          perks={['Top 5 placement', 'Featured badge on your card', 'Priority in the list']}
          disabled
          data-testid="tier-featured"
        />
        <TierOption
          name="Premium"
          price="£39"
          badge="⭐ Best exposure"
          perks={['Top placement', 'Highlighted card', 'Top-supplier badge']}
          disabled
          data-testid="tier-premium"
        />
        <p className="text-[10px] text-white/30 text-center">Paid placements launching soon — join free today to secure your spot.</p>
      </div>
    </div>
  </div>
);

const TierOption = ({ name, price, perks, badge, onPick, disabled, ...rest }) => (
  <button
    onClick={onPick}
    disabled={disabled}
    className={`w-full text-left rounded-xl p-4 border transition-all ${
      disabled
        ? 'bg-white/[0.02] border-white/5 opacity-60 cursor-not-allowed'
        : 'bg-white/[0.05] border-white/15 hover:border-[#B89762]/45/40 hover:bg-white/[0.08]'
    }`}
    {...rest}
  >
    <div className="flex items-start justify-between gap-2">
      <div>
        <p className="font-black text-sm flex items-center gap-2">
          {name}
          {badge && <span className="text-[9px] font-bold text-amber-300">{badge}</span>}
        </p>
        <p className="text-[11px] text-white/50 mt-0.5">
          {perks.map((p) => <span key={p} className="block">✓ {p}</span>)}
        </p>
      </div>
      <p className="font-black text-[#C29D52]">{price}</p>
    </div>
    {disabled && (
      <p className="mt-2 text-[10px] font-black text-amber-300/70 flex items-center gap-1">
        <Lock size={9} weight="fill" /> Coming soon
      </p>
    )}
  </button>
);
